/**
 * BPMN composer — pure projection of a `BpmnSceneInput` (the engine's
 * horizontal-BPMN grid layout, mirrored in ./types.ts) into flat, z-ordered
 * paint groups with RESOLVED colours and pixel positions. No React, no DOM,
 * no CSS: paint comes from `ProcessTokens`, pixels from `./metrics.js`.
 *
 * The visual language is a faithful port of the live `BpmnView` (committed
 * state, plan 2026-07-07-scene-process-render P1): tasks as rounded rects
 * with owner chips, gateways as diamonds with the BPMN 2.0 glyph set,
 * weighted event circles, open-bracket annotations, frames behind with
 * activity markers, and the default-flow slash. The orthogonal edge-path
 * generators (`bpmnEdgeGeom`, `reentryLift`, `defaultSlashD`, `gridCx`/`gridCy`,
 * `nodeHalfW`/`nodeHalfH`) are exported pure so the live view can consume the
 * SAME `d`-strings when it unifies onto this composer (plan P5).
 *
 * One deliberate departure from the committed state: gateway/event/frame
 * strokes tint by owner through `ownerColor` (gated `!accent && !isActor`,
 * events also `!compensation`) — tracking the in-flight live view's direction
 * (working-tree BpmnView.tsx 226/612/646), which the committed state predates.
 *
 * Interactivity is out of scope by design: no active-step ring, no pickable
 * cursor styling — those are live-shell overlays. `data-*` attributes are kept
 * on every group so tests and downstream tooling can address the anatomy.
 */

import {
  ANNOTATION_BRACKET_W,
  ANNOTATION_OWNER_DY,
  ANNOTATION_STROKE,
  ANNOTATION_TEXT_DX,
  ANNOTATION_TEXT_DY,
  ASSOC_ANCHOR_DX,
  ASSOC_ANCHOR_DY,
  CH,
  COMPENSATION_MARKER_DY,
  CW,
  DEFAULT_SLASH_DX1,
  DEFAULT_SLASH_DX2,
  DEFAULT_SLASH_DY1,
  DEFAULT_SLASH_DY2,
  DROP_ELBOW_DIVISOR,
  EDGE_DASH,
  EDGE_ELBOW,
  EDGE_LABEL_DY,
  EDGE_STROKE,
  EVENT_CANCEL_ARM,
  EVENT_CANCEL_STROKE,
  EVENT_CONDITIONAL_H,
  EVENT_CONDITIONAL_STROKE,
  EVENT_CONDITIONAL_W,
  EVENT_DOUBLE_RING_INSET,
  EVENT_DOUBLE_RING_STROKE,
  EVENT_LABEL_DY,
  EVENT_R,
  EVENT_STROKE,
  EVENT_STROKE_OWNER,
  EVENT_STROKE_THICK,
  EVENT_TERMINATE_INSET,
  EVENT_TIMER_R,
  EVENT_TIMER_STROKE,
  FONT_LABEL,
  FONT_SMALL,
  FONT_TITLE,
  FONT_WEIGHT_MEDIUM,
  FRAME_DASH_DEFAULT,
  FRAME_DASH_DIST,
  FRAME_INSET_H,
  FRAME_INSET_W,
  FRAME_INSET_X,
  FRAME_INSET_Y,
  FRAME_LABEL_DX,
  FRAME_LABEL_DY,
  FRAME_MARKER_DY,
  FRAME_RADIUS,
  GATE_LABEL_DY,
  GATE_R,
  GLYPH_AND_ARM,
  GLYPH_AND_STROKE,
  GLYPH_COMPLEX_ARM,
  GLYPH_COMPLEX_DIAG,
  GLYPH_COMPLEX_STROKE,
  GLYPH_EVENT_INNER_R,
  GLYPH_EVENT_OUTER_R,
  GLYPH_EVENT_PENTAGON_R,
  GLYPH_EVENT_STROKE,
  GLYPH_OR_R,
  GLYPH_OR_STROKE,
  GLYPH_XOR_ARM,
  GLYPH_XOR_STROKE,
  INBOUND_H,
  INBOUND_INSET_X,
  INBOUND_INSET_Y,
  INBOUND_STROKE,
  INBOUND_W,
  LANE_LABEL_CHAR_H,
  LANE_LABEL_PAD,
  LANE_LABEL_SIZE,
  LANE_RAIL_CELL_W,
  LANE_SEPARATOR_STROKE,
  POOL_BORDER_RADIUS,
  POOL_BORDER_STROKE,
  MARKER_LOOP_R,
  MARKER_LOOP_STROKE,
  MARKER_MULTI_BAR,
  MARKER_MULTI_GAP,
  MARKER_MULTI_STROKE,
  MARKER_SUBPROCESS_SIZE,
  MARKER_SUBPROCESS_STROKE,
  NODE_STROKE,
  NODE_STROKE_ACTIVE,
  OWNER_CHIP_DY,
  PAD,
  REENTRY_LIFT_DIVISOR,
  REENTRY_MIN_LIFT,
  SUBPROCESS_MARKER_DY,
  SYSTEM_CAP_RY,
  SYSTEM_FILL_ALPHA,
  SYSTEM_H,
  SYSTEM_LABEL_DY,
  SYSTEM_STROKE,
  SYSTEM_W,
  ENGINE_BADGE_W,
  ENGINE_BADGE_H,
  ENGINE_BADGE_R,
  ENGINE_BADGE_DX,
  ENGINE_BADGE_DY,
  ENGINE_STUB,
  ENGINE_STROKE,
  ENGINE_FILL_ALPHA,
  LABEL_W_ENGINE,
  TASK_H,
  TASK_LABEL_DY,
  TASK_RADIUS,
  TASK_W,
  LABEL_W_ANNOTATION,
  LABEL_W_SYSTEM,
  LABEL_W_DIFF_DETAIL,
  LABEL_W_EDGE,
  LABEL_W_EVENT,
  LABEL_W_FRAME,
  LABEL_W_GATEWAY,
  LABEL_W_OWNER,
  LABEL_W_TASK,
} from "./metrics.js";
import { alpha } from "../tokens.js";
import { ellipsize } from "../text.js";
import { metricsMeasure } from "../metrics.js";
import { defaultProcessTokens, type ProcessTokens } from "./tokens.js";
import type {
  BpmnEdge,
  BpmnFrame,
  BpmnNode,
  BpmnOwner,
  BpmnSceneInput,
  ScaffoldDiffTag,
} from "./types.js";

// ── Paint primitives ─────────────────────────────────────────────────────────

export interface RectShape {
  readonly kind: "rect";
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly rx?: number;
  readonly fill: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly strokeOpacity?: number;
  readonly dash?: string;
}

export interface CircleShape {
  readonly kind: "circle";
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly fill: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly dash?: string;
}

export interface PolygonShape {
  readonly kind: "polygon";
  readonly points: string;
  readonly fill: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly dash?: string;
}

export interface PathShape {
  readonly kind: "path";
  readonly d: string;
  readonly fill: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly strokeOpacity?: number;
  /** Whole-shape opacity (committed aux paths fade to 0.95 while their
   *  labels stay full — per-shape, NOT on the group). */
  readonly opacity?: number;
  readonly dash?: string;
  /** Arrowhead at the path end. `solid`/`open` are the BPMN heads; the `sw*`
   *  names are the swimlane family (committed SwimlaneView marker defs). */
  readonly marker?: "solid" | "open" | "swArrow" | "swFlowTick" | "swFlowTickErr" | "swCompTick" | "swReentryTick";
}

export interface LineShape {
  readonly kind: "line";
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly strokeOpacity?: number;
  readonly dash?: string;
}

export interface TextShape {
  readonly kind: "text";
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly fill: string;
  readonly size: number;
  readonly anchor?: "middle" | "end";
  readonly italic?: boolean;
  readonly weight?: number;
  /** Paint-order halo colour behind the glyphs (`paint-order="stroke"`,
   *  3px round-joined) — the committed overlay-label legibility trick
   *  (SwimlaneView 1616-1625 / 1646-1659 / 1729-1742). */
  readonly halo?: string;
  /** Rotation about (cx, cy) — rail lane/pool labels read bottom-up (-90°). */
  readonly rotate?: { readonly deg: number; readonly cx: number; readonly cy: number };
}

export type BpmnShape = RectShape | CircleShape | PolygonShape | PathShape | LineShape | TextShape;

/** One z-atom: a node, an edge, or a frame, with its `data-*` identity.
 *  `undefined` attr values are skipped at serialization (numbers rounded). */
export interface BpmnGroup {
  readonly attrs: Readonly<Record<string, string | number | undefined>>;
  readonly opacity?: number;
  /** Hover tooltip (`<title>`), mirroring the live view's full-label titles. */
  readonly title?: string;
  readonly shapes: readonly BpmnShape[];
}

export interface ComposedBpmn {
  /** Total canvas size in px, headroom + lane rail included. */
  readonly width: number;
  readonly height: number;
  /** Vertical space reserved above the top track for `go` back-arc apexes;
   *  content is drawn translated down by this much. */
  readonly headroom: number;
  /** Horizontal space reserved LEFT of the grid for the lane rail (pool
   *  spines + lane labels); 0 when laneless. Grid content is drawn translated
   *  right by this much; `rail` groups are NOT (they own the rail strip and
   *  full-width separators). */
  readonly railWidth: number;
  /** Lane rail chrome — band separators, pool borders/spines, lane labels.
   *  Painted below `frames`. Empty when laneless. */
  readonly rail: readonly BpmnGroup[];
  /** z-order: rail (bottom) → frames → edges → nodes (top). */
  readonly frames: readonly BpmnGroup[];
  readonly edges: readonly BpmnGroup[];
  readonly nodes: readonly BpmnGroup[];
  /** Which arrowhead defs the edge set actually uses. */
  readonly markers: { readonly solid: boolean; readonly open: boolean };
}

export interface ComposeBpmnOptions {
  readonly tokens?: ProcessTokens;
  /**
   * Owner colour: chips (task/annotation) and the gateway/event/frame stroke
   * tint. Return `undefined` to defer to chrome.
   *
   * Deferring is the point of the optional return: an ACTOR is chrome
   * (`tokens.actorText`), not a model value, so a caller supplies only what it
   * uniquely knows — this owner's colour — and cannot get the rest subtly wrong.
   * Callers used to re-derive both, and one of them got it wrong.
   *
   * Scene never derives a colour from a model value: it cannot reach the
   * palette's `swatchFor` (it is dependency-free by design), and guessing would
   * put a second, disagreeing hash in the product. An owner nobody colours is
   * therefore painted as chrome, not as an invented tint.
   */
  readonly ownerColor?: (owner: BpmnOwner) => string | undefined;
}

// ── Shared grid / edge geometry (pure; reused by the live view in P5) ────────

/** 2dp — path `d` / polygon `points` strings must honor the renderer's
 *  rounding contract at GENERATION time, so the live view gets the same
 *  rounded output from these exported geometry fns (P5). */
const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Grid column → node centre x. */
export function gridCx(x: number): number {
  return PAD + x * CW + CW / 2;
}
/** Grid track → node centre y. */
export function gridCy(y: number): number {
  return PAD + y * CH + CH / 2;
}

/** Entry/exit half-width of a node, for edge endpoint placement. */
export function nodeHalfW(n: BpmnNode): number {
  if (n.kind === "task") return TASK_W / 2;
  if (n.kind === "gateway") return GATE_R;
  if (n.kind === "annotation") return TASK_W / 2;
  return EVENT_R;
}
export function nodeHalfH(n: BpmnNode): number {
  if (n.kind === "task") return TASK_H / 2;
  if (n.kind === "gateway") return GATE_R;
  if (n.kind === "annotation") return TASK_H / 2;
  return EVENT_R;
}

/** Arc lift of a `go` back-edge — shared by the path and the headroom calc. */
export function reentryLift(colSpan: number): number {
  return r2(Math.max(REENTRY_MIN_LIFT, (colSpan * CW) / REENTRY_LIFT_DIVISOR));
}

export interface BpmnEdgeGeom {
  readonly d: string;
  /** Departure point + orientation, for the default-flow slash. */
  readonly sx: number;
  readonly sy: number;
  readonly horizontal: boolean;
  /** A point ON the connector (visual middle) — the anchor for an on-edge
   *  affordance (e.g. the edit-mode "insert step here" "+" between two steps). */
  readonly mx: number;
  readonly my: number;
}

/** The BPMN default-flow marker: a slash across the flow near its source. */
export function defaultSlashD(sx: number, sy: number): string {
  return `M ${sx + DEFAULT_SLASH_DX1} ${sy + DEFAULT_SLASH_DY1} L ${sx + DEFAULT_SLASH_DX2} ${sy - DEFAULT_SLASH_DY2}`;
}

/**
 * Orthogonal route for an edge. Ports the live view's `edgeGeom` verbatim:
 * seq flows leave right / land left (mirrored when the target sits left of the
 * source — compensation chains fire backwards), `drop`s fall bottom → top,
 * `reentry` arcs over the top, and a from-a-FRAME association (try frame →
 * boundary event) departs the frame's lower-left region.
 */
export function bpmnEdgeGeom(input: BpmnSceneInput, e: BpmnEdge): BpmnEdgeGeom | null {
  const byId = new Map<string, BpmnNode>(input.nodes.map((n) => [n.id, n]));
  const a = byId.get(e.from);
  const b = byId.get(e.to);
  if (!a && !b) return null;
  if (!a) {
    const f = input.frames.find((fr) => fr.id === e.from);
    const bb = b!;
    if (!f) return null;
    const sx = PAD + f.x0 * CW + ASSOC_ANCHOR_DX;
    const sy = PAD + f.y1 * CH + CH - ASSOC_ANCHOR_DY;
    const ex0 = gridCx(bb.x) - nodeHalfW(bb);
    const ey0 = gridCy(bb.y);
    return { d: `M ${sx} ${sy} L ${ex0} ${ey0}`, sx, sy, horizontal: false, mx: (sx + ex0) / 2, my: (sy + ey0) / 2 };
  }
  if (!b) return null;

  const sx = gridCx(a.x) + nodeHalfW(a);
  const sy = gridCy(a.y);
  const ex = gridCx(b.x) - nodeHalfW(b);
  const ey = gridCy(b.y);

  if (e.style === "reentry") {
    const sx2 = gridCx(a.x);
    const sy2 = gridCy(a.y) - nodeHalfH(a);
    const ex2 = gridCx(b.x);
    const ey2 = gridCy(b.y) - nodeHalfH(b);
    const lift = reentryLift(Math.abs(a.x - b.x));
    return {
      d: `M ${sx2} ${sy2} C ${sx2} ${sy2 - lift}, ${ex2} ${ey2 - lift}, ${ex2} ${ey2}`,
      sx: sx2,
      sy: sy2,
      horizontal: false,
      mx: (sx2 + ex2) / 2,
      my: Math.min(sy2, ey2) - lift / 2,
    };
  }
  if (e.style === "drop") {
    const sx2 = gridCx(a.x);
    const sy2 = gridCy(a.y) + nodeHalfH(a);
    const ex2 = gridCx(b.x);
    const ey2 = gridCy(b.y) - nodeHalfH(b);
    if (sx2 === ex2) return { d: `M ${sx2} ${sy2} L ${ex2} ${ey2}`, sx: sx2, sy: sy2, horizontal: false, mx: sx2, my: (sy2 + ey2) / 2 };
    const midY = r2(ey2 - CH / DROP_ELBOW_DIVISOR);
    return { d: `M ${sx2} ${sy2} V ${midY} H ${ex2} V ${ey2}`, sx: sx2, sy: sy2, horizontal: false, mx: (sx2 + ex2) / 2, my: midY };
  }
  if (b.x < a.x) {
    const sxL = gridCx(a.x) - nodeHalfW(a);
    const exR = gridCx(b.x) + nodeHalfW(b);
    if (sy === ey) return { d: `M ${sxL} ${sy} H ${exR}`, sx: sxL, sy, horizontal: true, mx: (sxL + exR) / 2, my: sy };
    const midX = exR + EDGE_ELBOW;
    return { d: `M ${sxL} ${sy} H ${midX} V ${ey} H ${exR}`, sx: sxL, sy, horizontal: true, mx: midX, my: (sy + ey) / 2 };
  }
  if (sy === ey) return { d: `M ${sx} ${sy} H ${ex}`, sx, sy, horizontal: true, mx: (sx + ex) / 2, my: sy };
  const midX = ex - EDGE_ELBOW;
  return { d: `M ${sx} ${sy} H ${midX} V ${ey} H ${ex}`, sx, sy, horizontal: true, mx: midX, my: (sy + ey) / 2 };
}

// ── Internals ────────────────────────────────────────────────────────────────

/**
 * Ellipsize a single-line BPMN label to the PIXEL width its box has — shared
 * by the composer and the live BpmnView so both cut at the same glyph.
 *
 * Measured with scene's `metricsMeasure` (never the DOM), which carries real
 * Inter advances for the Cyrillic band: a char budget cut mixed-script labels
 * far too late and they overflowed their box.
 */
export function fitLabel(
  s: string,
  maxWidth: number,
  size: number,
  family: string,
  weight = 400,
): string {
  return ellipsize(s, maxWidth, { family, size, weight }, metricsMeasure);
}

interface DiffAccent {
  readonly stroke: string | null;
  readonly dash?: string;
  readonly ghost?: boolean;
}

function diffAccent(t: ProcessTokens, diff?: ScaffoldDiffTag): DiffAccent {
  if (!diff) return { stroke: null };
  if (diff.status === "added") return { stroke: t.diffAdded };
  if (diff.status === "removed") return { stroke: t.diffRemoved, dash: "5 4", ghost: true };
  if (diff.status === "changed") return { stroke: t.diffChanged };
  return { stroke: null };
}

/** The `changed` diff-detail chip below a node. */
function diffChip(t: ProcessTokens, x: number, y: number, diff?: ScaffoldDiffTag): TextShape[] {
  if (!diff || diff.status !== "changed" || !diff.detail) return [];
  return [
    {
      kind: "text",
      x,
      y,
      text: fitLabel(diff.detail, LABEL_W_DIFF_DETAIL, FONT_SMALL, t.fontFamily),
      fill: t.diffChanged,
      size: FONT_SMALL,
      anchor: "middle",
    },
  ];
}

/** BPMN activity markers: loop ↻ / sequential MI / parallel MI / subprocess ⊞. */
function activityMarker(
  t: ProcessTokens,
  x: number,
  y: number,
  marker: "loop" | "multi" | "multi-parallel" | "subprocess",
): BpmnShape[] {
  if (marker === "loop") {
    return [
      {
        kind: "path",
        d: `M ${x - MARKER_LOOP_R} ${y} a ${MARKER_LOOP_R} ${MARKER_LOOP_R} 0 1 0 2 -4.4`,
        fill: "none",
        stroke: t.markerStroke,
        strokeWidth: MARKER_LOOP_STROKE,
      },
      { kind: "path", d: `M ${x - 4.6} ${y - 5.8} l -1.8 2 l 2.6 0.8 z`, fill: t.markerFill },
    ];
  }
  if (marker === "multi") {
    return [-MARKER_MULTI_GAP, 0, MARKER_MULTI_GAP].map((dy) => ({
      kind: "line" as const,
      x1: x - MARKER_MULTI_BAR,
      y1: y + dy,
      x2: x + MARKER_MULTI_BAR,
      y2: y + dy,
      stroke: t.markerStroke,
      strokeWidth: MARKER_MULTI_STROKE,
    }));
  }
  if (marker === "multi-parallel") {
    return [-MARKER_MULTI_GAP, 0, MARKER_MULTI_GAP].map((dx) => ({
      kind: "line" as const,
      x1: x + dx,
      y1: y - MARKER_MULTI_BAR,
      x2: x + dx,
      y2: y + MARKER_MULTI_BAR,
      stroke: t.markerStroke,
      strokeWidth: MARKER_MULTI_STROKE,
    }));
  }
  const s = MARKER_SUBPROCESS_SIZE / 2;
  return [
    {
      kind: "rect",
      x: x - s,
      y: y - s,
      w: MARKER_SUBPROCESS_SIZE,
      h: MARKER_SUBPROCESS_SIZE,
      rx: 1.5,
      fill: "none",
      stroke: t.markerStroke,
      strokeWidth: MARKER_SUBPROCESS_STROKE,
    },
    { kind: "line", x1: x - 3.5, y1: y, x2: x + 3.5, y2: y, stroke: t.markerStroke, strokeWidth: MARKER_SUBPROCESS_STROKE },
    { kind: "line", x1: x, y1: y - 3.5, x2: x, y2: y + 3.5, stroke: t.markerStroke, strokeWidth: MARKER_SUBPROCESS_STROKE },
  ];
}

/** BPMN 2.0 gateway glyphs, drawn (not text): ×, +, ◯, ✳, event pentagon. */
function gatewayGlyph(t: ProcessTokens, x: number, y: number, variant: string): BpmnShape[] {
  const stroke = t.glyphStroke;
  if (variant === "xor") {
    return [
      {
        kind: "path",
        d: `M ${x - GLYPH_XOR_ARM} ${y - GLYPH_XOR_ARM} L ${x + GLYPH_XOR_ARM} ${y + GLYPH_XOR_ARM} M ${x - GLYPH_XOR_ARM} ${y + GLYPH_XOR_ARM} L ${x + GLYPH_XOR_ARM} ${y - GLYPH_XOR_ARM}`,
        fill: "none",
        stroke,
        strokeWidth: GLYPH_XOR_STROKE,
      },
    ];
  }
  if (variant === "and") {
    return [
      {
        kind: "path",
        d: `M ${x} ${y - GLYPH_AND_ARM} V ${y + GLYPH_AND_ARM} M ${x - GLYPH_AND_ARM} ${y} H ${x + GLYPH_AND_ARM}`,
        fill: "none",
        stroke,
        strokeWidth: GLYPH_AND_STROKE,
      },
    ];
  }
  if (variant === "or") {
    return [{ kind: "circle", cx: x, cy: y, r: GLYPH_OR_R, fill: "none", stroke, strokeWidth: GLYPH_OR_STROKE }];
  }
  if (variant === "complex") {
    return [
      {
        kind: "path",
        d: `M ${x} ${y - GLYPH_COMPLEX_ARM} V ${y + GLYPH_COMPLEX_ARM} M ${x - GLYPH_COMPLEX_ARM} ${y} H ${x + GLYPH_COMPLEX_ARM} M ${x - GLYPH_COMPLEX_DIAG} ${y - GLYPH_COMPLEX_DIAG} L ${x + GLYPH_COMPLEX_DIAG} ${y + GLYPH_COMPLEX_DIAG} M ${x - GLYPH_COMPLEX_DIAG} ${y + GLYPH_COMPLEX_DIAG} L ${x + GLYPH_COMPLEX_DIAG} ${y - GLYPH_COMPLEX_DIAG}`,
        fill: "none",
        stroke,
        strokeWidth: GLYPH_COMPLEX_STROKE,
      },
    ];
  }
  // Event-based: double circle + pentagon.
  const p = GLYPH_EVENT_PENTAGON_R;
  const pts = [
    [0, -p],
    [p * 0.951, -p * 0.309],
    [p * 0.588, p * 0.809],
    [-p * 0.588, p * 0.809],
    [-p * 0.951, -p * 0.309],
  ]
    .map(([dx, dy]) => `${r2(x + dx!)},${r2(y + dy!)}`)
    .join(" ");
  return [
    { kind: "circle", cx: x, cy: y, r: GLYPH_EVENT_OUTER_R, fill: "none", stroke, strokeWidth: GLYPH_EVENT_STROKE },
    { kind: "circle", cx: x, cy: y, r: GLYPH_EVENT_INNER_R, fill: "none", stroke, strokeWidth: GLYPH_EVENT_STROKE },
    { kind: "polygon", points: pts, fill: "none", stroke, strokeWidth: GLYPH_EVENT_STROKE },
  ];
}

/** Event trigger/result glyphs, drawn to BPMN convention. */
function eventGlyph(t: ProcessTokens, x: number, y: number, node: Extract<BpmnNode, { kind: "event" }>): BpmnShape[] {
  const stroke = t.glyphStroke;
  if (node.event === "end-error" || node.event === "boundary-error") {
    return [
      {
        kind: "path",
        d: `M ${x + 2.5} ${y - 7} L ${x - 4.5} ${y + 1.5} H ${x - 1} L ${x - 2.5} ${y + 7} L ${x + 4.5} ${y - 1.5} H ${x + 1} Z`,
        fill: t.glyphFill,
      },
    ];
  }
  if (node.event === "end-cancel") {
    return [
      {
        kind: "path",
        d: `M ${x - EVENT_CANCEL_ARM} ${y - EVENT_CANCEL_ARM} L ${x + EVENT_CANCEL_ARM} ${y + EVENT_CANCEL_ARM} M ${x - EVENT_CANCEL_ARM} ${y + EVENT_CANCEL_ARM} L ${x + EVENT_CANCEL_ARM} ${y - EVENT_CANCEL_ARM}`,
        fill: "none",
        stroke,
        strokeWidth: EVENT_CANCEL_STROKE,
      },
    ];
  }
  if (node.event === "link") {
    return [
      {
        kind: "path",
        d: `M ${x - 6} ${y - 3} H ${x + 1} V ${y - 6} L ${x + 7} ${y} L ${x + 1} ${y + 6} V ${y + 3} H ${x - 6} Z`,
        fill: "none",
        stroke,
        strokeWidth: 1.4,
      },
    ];
  }
  if (node.trigger === "timer") {
    return [
      { kind: "circle", cx: x, cy: y, r: EVENT_TIMER_R, fill: "none", stroke, strokeWidth: EVENT_TIMER_STROKE },
      {
        kind: "path",
        d: `M ${x} ${y - 4.5} V ${y} L ${x + 3.5} ${y + 2}`,
        fill: "none",
        stroke,
        strokeWidth: EVENT_TIMER_STROKE,
      },
    ];
  }
  if (node.trigger === "conditional") {
    const shapes: BpmnShape[] = [
      {
        kind: "rect",
        x: x - EVENT_CONDITIONAL_W / 2,
        y: y - EVENT_CONDITIONAL_H / 2,
        w: EVENT_CONDITIONAL_W,
        h: EVENT_CONDITIONAL_H,
        fill: "none",
        stroke,
        strokeWidth: EVENT_CONDITIONAL_STROKE,
      },
    ];
    for (const dy of [-3.5, -1, 1.5, 4]) {
      shapes.push({ kind: "line", x1: x - 3, y1: y + dy, x2: x + 3, y2: y + dy, stroke, strokeWidth: 1 });
    }
    return shapes;
  }
  return [];
}

// ── Node composition ─────────────────────────────────────────────────────────

function composeTask(
  t: ProcessTokens,
  ownerColor: (o: BpmnOwner) => string,
  n: Extract<BpmnNode, { kind: "task" }>,
): BpmnGroup {
  const x = gridCx(n.x);
  const y = gridCy(n.y);
  const isSubprocess = !!n.subprocess;
  const accent = diffAccent(t, n.diff);
  const shapes: BpmnShape[] = [
    {
      kind: "rect",
      x: x - TASK_W / 2,
      y: y - TASK_H / 2,
      w: TASK_W,
      h: TASK_H,
      rx: TASK_RADIUS,
      fill: n.compensation ? t.compensationFill : t.taskFill,
      stroke: accent.stroke ?? (n.compensation ? t.compensationStroke : t.taskStroke),
      strokeWidth: accent.stroke ? NODE_STROKE_ACTIVE : NODE_STROKE,
      dash: accent.dash,
    },
    ...diffChip(t, x, y + TASK_H / 2 + 12, n.diff),
    {
      kind: "text",
      x,
      y: y + TASK_LABEL_DY,
      text: fitLabel(n.label, LABEL_W_TASK, FONT_TITLE, t.fontFamily),
      fill: t.titleText,
      size: FONT_TITLE,
      anchor: "middle",
    },
  ];
  if (n.owner) {
    shapes.push({
      kind: "text",
      x: x - TASK_W / 2,
      y: y - TASK_H / 2 - OWNER_CHIP_DY,
      text: fitLabel(n.owner.label, LABEL_W_OWNER, FONT_LABEL, t.fontFamily),
      fill: ownerColor(n.owner),
      size: FONT_LABEL,
      italic: n.owner.isActor,
    });
  }
  if (n.inbound) {
    const ex = x + TASK_W / 2 - INBOUND_INSET_X;
    const ey = y - TASK_H / 2 + INBOUND_INSET_Y;
    shapes.push(
      { kind: "rect", x: ex, y: ey, w: INBOUND_W, h: INBOUND_H, fill: "none", stroke: t.inboundStroke, strokeWidth: INBOUND_STROKE },
      {
        kind: "path",
        d: `M ${ex} ${ey} l ${INBOUND_W / 2} 5 l ${INBOUND_W / 2} -5`,
        fill: "none",
        stroke: t.inboundStroke,
        strokeWidth: INBOUND_STROKE,
      },
    );
  }
  if (isSubprocess) shapes.push(...activityMarker(t, x, y + TASK_H / 2 - SUBPROCESS_MARKER_DY, "subprocess"));
  if (n.compensation) {
    const my = y - TASK_H / 2 + COMPENSATION_MARKER_DY;
    shapes.push({
      kind: "path",
      d: `M ${x - 1} ${my} l -5 -3.5 v 7 z M ${x + 5} ${my} l -5 -3.5 v 7 z`,
      fill: t.compensationMarker,
    });
  }
  return {
    attrs: pruned({
      "data-node-id": n.id,
      "data-node-kind": isSubprocess ? "subprocess" : "task",
      "data-step-index": n.stepIndex != null ? String(n.stepIndex) : undefined,
      "data-dimmed": n.dimmed ? "true" : undefined,
      "data-diff": n.diff?.status,
    }),
    opacity: accent.ghost ? t.ghostOpacity : n.dimmed ? t.dimOpacity : undefined,
    // A changed node's hover tooltip is the change summary, not the label —
    // the committed view emits the DiffChip <title> before the label <title>
    // and the first one wins (BpmnView.tsx 148-158).
    title: n.diff?.status === "changed" && n.diff.detail ? n.diff.detail : n.label,
    shapes,
  };
}

/**
 * `engine @@<key>` — a small caller-derived aspect chip dangling off a task's
 * top-right corner, joined by a short stub. Node-attached (its own `<g>`, but
 * NOT its own grid node), tinted by the caller's aspect-value colour. Copies of
 * one value share `data-logical-id` (`eng:<value>`) so the live shell fans
 * selection/hover out to the one logical place, mirroring `composeSystem`.
 */
function composeEngineBadge(t: ProcessTokens, n: Extract<BpmnNode, { kind: "task" }>): BpmnGroup {
  const e = n.danglingEngine!;
  const x = gridCx(n.x);
  const y = gridCy(n.y);
  const left = x + TASK_W / 2 + ENGINE_BADGE_DX - ENGINE_BADGE_W;
  const top = y - TASK_H / 2 - ENGINE_BADGE_DY - ENGINE_BADGE_H;
  const cx = left + ENGINE_BADGE_W / 2;
  const accent = diffAccent(t, n.diff);
  const stroke = accent.stroke ?? e.color;
  const shapes: BpmnShape[] = [
    // Stub: from the task's top edge up to the chip's bottom.
    { kind: "line", x1: cx, y1: y - TASK_H / 2, x2: cx, y2: top + ENGINE_BADGE_H, stroke, strokeWidth: ENGINE_STROKE },
    {
      kind: "rect",
      x: left,
      y: top,
      w: ENGINE_BADGE_W,
      h: ENGINE_BADGE_H,
      rx: ENGINE_BADGE_R,
      fill: alpha(e.color, ENGINE_FILL_ALPHA),
      stroke,
      strokeWidth: accent.stroke ? NODE_STROKE_ACTIVE : ENGINE_STROKE,
      dash: accent.dash,
    },
    {
      kind: "text",
      x: cx,
      y: top + ENGINE_BADGE_H / 2 + 3,
      text: fitLabel(e.value, LABEL_W_ENGINE, FONT_SMALL, t.fontFamily),
      fill: e.color,
      size: FONT_SMALL,
      anchor: "middle",
    },
  ];
  return {
    attrs: pruned({
      "data-node-id": `${n.id}::eng`,
      "data-node-kind": "engine",
      "data-logical-id": e.logicalId,
      "data-aspect-value": e.value,
      "data-dimmed": n.dimmed ? "true" : undefined,
      "data-diff": n.diff?.status,
    }),
    opacity: accent.ghost ? t.ghostOpacity : n.dimmed ? t.dimOpacity : undefined,
    title: e.value,
    shapes,
  };
}

/**
 * A `replicate`d aspect place — a small data-store cylinder half a track below
 * its consuming task (aspect render mode `replicated`). Tinted by the aspect
 * value's colour (carried on the node, from the caller's `aspectColorFor`, so
 * board `web`/`replicated` and process agree). `data-logical-id` lets the live
 * shell fan selection/hover/diff out to the one logical node behind the copies.
 */
function composeSystem(t: ProcessTokens, n: Extract<BpmnNode, { kind: "system" }>): BpmnGroup {
  const cx = gridCx(n.x);
  const cy = gridCy(n.y);
  const accent = diffAccent(t, n.diff);
  const stroke = accent.stroke ?? n.accent;
  const w = SYSTEM_W;
  const h = SYSTEM_H;
  const rx = w / 2;
  const ry = SYSTEM_CAP_RY;
  const left = cx - rx;
  const right = cx + rx;
  const capY = cy - h / 2 + ry; // centre-line of the top cap
  const baseY = cy + h / 2 - ry; // centre-line of the (front) bottom arc
  const fill = alpha(n.accent, SYSTEM_FILL_ALPHA);
  const shapes: BpmnShape[] = [
    // Body: side walls + front bottom arc.
    {
      kind: "path",
      d: `M ${left} ${capY} V ${baseY} A ${rx} ${ry} 0 0 0 ${right} ${baseY} V ${capY}`,
      fill,
      stroke,
      strokeWidth: accent.stroke ? NODE_STROKE_ACTIVE : SYSTEM_STROKE,
      dash: accent.dash,
    },
    // Top cap ellipse (two half-arcs).
    {
      kind: "path",
      d: `M ${left} ${capY} A ${rx} ${ry} 0 0 0 ${right} ${capY} A ${rx} ${ry} 0 0 0 ${left} ${capY} Z`,
      fill,
      stroke,
      strokeWidth: accent.stroke ? NODE_STROKE_ACTIVE : SYSTEM_STROKE,
      dash: accent.dash,
    },
    {
      kind: "text",
      x: cx,
      y: cy + h / 2 + SYSTEM_LABEL_DY,
      text: fitLabel(n.label, LABEL_W_SYSTEM, FONT_SMALL, t.fontFamily),
      fill: t.titleText,
      size: FONT_SMALL,
      anchor: "middle",
    },
  ];
  return {
    attrs: pruned({
      "data-node-id": n.id,
      "data-node-kind": "system",
      "data-logical-id": n.logicalId,
      "data-aspect-value": n.accentValue,
      "data-dimmed": n.dimmed ? "true" : undefined,
      "data-diff": n.diff?.status,
    }),
    opacity: accent.ghost ? t.ghostOpacity : n.dimmed ? t.dimOpacity : undefined,
    title: n.label,
    shapes,
  };
}

function composeGateway(
  t: ProcessTokens,
  ownerColor: (o: BpmnOwner) => string,
  n: Extract<BpmnNode, { kind: "gateway" }>,
): BpmnGroup {
  const x = gridCx(n.x);
  const y = gridCy(n.y);
  const accent = diffAccent(t, n.diff);
  // Owner tint on the diamond stroke — diff accent wins, actors never tint
  // (working-tree BpmnView.tsx 612).
  const oc = !accent.stroke && n.owner && !n.owner.isActor ? ownerColor(n.owner) : undefined;
  const shapes: BpmnShape[] = [
    {
      kind: "polygon",
      points: `${x},${y - GATE_R} ${x + GATE_R},${y} ${x},${y + GATE_R} ${x - GATE_R},${y}`,
      fill: t.gatewayFill,
      stroke: accent.stroke ?? oc ?? t.gatewayStroke,
      strokeWidth: accent.stroke || oc ? NODE_STROKE_ACTIVE : NODE_STROKE,
      dash: accent.dash,
    },
    ...gatewayGlyph(t, x, y, n.variant),
  ];
  if (n.label) {
    shapes.push({
      kind: "text",
      x,
      y: y - GATE_R - GATE_LABEL_DY,
      text: fitLabel(n.label, LABEL_W_GATEWAY, FONT_LABEL, t.fontFamily),
      fill: t.labelText,
      size: FONT_LABEL,
      anchor: "middle",
      italic: true,
    });
  }
  return {
    attrs: pruned({
      "data-node-id": n.id,
      "data-node-kind": "gateway",
      "data-gateway": `${n.gate}:${n.variant}`,
      "data-diff": n.diff?.status,
      "data-owner-colored": oc ? "true" : undefined,
    }),
    opacity: accent.ghost ? t.ghostOpacity : undefined,
    shapes,
  };
}

function composeEvent(
  t: ProcessTokens,
  ownerColor: (o: BpmnOwner) => string,
  n: Extract<BpmnNode, { kind: "event" }>,
): BpmnGroup {
  const x = gridCx(n.x);
  const y = gridCy(n.y);
  const thick = n.event.startsWith("end");
  const doubleRing = n.event === "intermediate" || n.event === "link" || n.event === "boundary-error";
  const accent = diffAccent(t, n.diff);
  // Owner tint on the ring — diff accent and the compensation amber win,
  // actors never tint (working-tree BpmnView.tsx 646).
  const oc = !accent.stroke && !n.compensation && n.owner && !n.owner.isActor ? ownerColor(n.owner) : undefined;
  const shapes: BpmnShape[] = [
    {
      kind: "circle",
      cx: x,
      cy: y,
      r: EVENT_R,
      fill: t.eventFill,
      stroke: accent.stroke ?? oc ?? (n.compensation ? t.eventCompStroke : t.eventStroke),
      strokeWidth: thick ? EVENT_STROKE_THICK : oc ? EVENT_STROKE_OWNER : EVENT_STROKE,
      dash: accent.dash,
    },
  ];
  if (doubleRing) {
    shapes.push({
      kind: "circle",
      cx: x,
      cy: y,
      r: EVENT_R - EVENT_DOUBLE_RING_INSET,
      fill: "none",
      stroke: t.eventStroke,
      strokeWidth: EVENT_DOUBLE_RING_STROKE,
    });
  }
  if (n.event === "end-terminate") {
    shapes.push({ kind: "circle", cx: x, cy: y, r: EVENT_R - EVENT_TERMINATE_INSET, fill: t.eventStroke });
  }
  shapes.push(...eventGlyph(t, x, y, n));
  if (n.label) {
    shapes.push({
      kind: "text",
      x,
      y: y + EVENT_R + EVENT_LABEL_DY,
      text: fitLabel(n.label, LABEL_W_EVENT, FONT_LABEL, t.fontFamily),
      fill: t.labelText,
      size: FONT_LABEL,
      anchor: "middle",
    });
  }
  return {
    attrs: pruned({
      "data-node-id": n.id,
      "data-node-kind": "event",
      "data-event": n.event,
      "data-trigger": n.trigger,
      "data-step-index": n.stepIndex != null ? String(n.stepIndex) : undefined,
      "data-dimmed": n.dimmed ? "true" : undefined,
      "data-diff": n.diff?.status,
      "data-owner-colored": oc ? "true" : undefined,
    }),
    opacity: accent.ghost ? t.ghostOpacity : n.dimmed ? t.dimOpacity : undefined,
    title: n.label,
    shapes,
  };
}

function composeAnnotation(
  t: ProcessTokens,
  ownerColor: (o: BpmnOwner) => string,
  n: Extract<BpmnNode, { kind: "annotation" }>,
): BpmnGroup {
  const x = gridCx(n.x);
  const y = gridCy(n.y);
  const ax = x - TASK_W / 2;
  const accent = diffAccent(t, n.diff);
  const shapes: BpmnShape[] = [
    {
      kind: "path",
      d: `M ${ax + ANNOTATION_BRACKET_W} ${y - TASK_H / 2 + 8} H ${ax} V ${y + TASK_H / 2 - 8} H ${ax + ANNOTATION_BRACKET_W}`,
      fill: "none",
      stroke: t.annotationStroke,
      strokeWidth: ANNOTATION_STROKE,
    },
    {
      kind: "text",
      x: ax + ANNOTATION_TEXT_DX,
      y: y + ANNOTATION_TEXT_DY,
      text: fitLabel(n.text, LABEL_W_ANNOTATION, FONT_LABEL, t.fontFamily),
      fill: t.annotationText,
      size: FONT_LABEL,
      italic: true,
    },
  ];
  if (n.owner) {
    shapes.push({
      kind: "text",
      x: ax,
      y: y - TASK_H / 2 + ANNOTATION_OWNER_DY,
      text: fitLabel(n.owner.label, LABEL_W_OWNER, FONT_SMALL, t.fontFamily),
      fill: ownerColor(n.owner),
      size: FONT_SMALL,
    });
  }
  return {
    attrs: pruned({
      "data-node-id": n.id,
      "data-node-kind": "annotation",
      "data-step-index": String(n.stepIndex),
      "data-dimmed": n.dimmed ? "true" : undefined,
      "data-diff": n.diff?.status,
    }),
    opacity: accent.ghost ? t.ghostOpacity : n.dimmed ? t.dimOpacity : undefined,
    title: n.text,
    shapes,
  };
}

// ── Frame / edge composition ─────────────────────────────────────────────────

function composeFrame(t: ProcessTokens, ownerColor: (o: BpmnOwner) => string, f: BpmnFrame): BpmnGroup {
  const x = PAD + f.x0 * CW + FRAME_INSET_X;
  const y = PAD + f.y0 * CH + FRAME_INSET_Y;
  const fw = (f.x1 - f.x0 + 1) * CW - FRAME_INSET_W;
  const fh = (f.y1 - f.y0 + 1) * CH - FRAME_INSET_H;
  const dash = f.kind === "dist" ? FRAME_DASH_DIST : f.kind === "do" ? undefined : FRAME_DASH_DEFAULT;
  const accent = diffAccent(t, f.diff);
  // Owner tint on the frame border — diff accent wins, actors never tint
  // (working-tree BpmnView.tsx 226); width stays default, matching the live rect.
  const oc = !accent.stroke && f.owner && !f.owner.isActor ? ownerColor(f.owner) : undefined;
  const shapes: BpmnShape[] = [
    {
      kind: "rect",
      x,
      y,
      w: fw,
      h: fh,
      rx: FRAME_RADIUS,
      fill: "none",
      stroke: accent.stroke ?? oc ?? t.frameStroke,
      dash: accent.dash ?? dash,
    },
    {
      kind: "text",
      x: x + FRAME_LABEL_DX,
      y: y + FRAME_LABEL_DY,
      text: fitLabel(f.label, LABEL_W_FRAME, FONT_LABEL, t.fontFamily, FONT_WEIGHT_MEDIUM),
      fill: t.mutedText,
      size: FONT_LABEL,
      weight: FONT_WEIGHT_MEDIUM,
    },
  ];
  if (f.kind === "each") shapes.push(...activityMarker(t, x + fw / 2, y + fh - FRAME_MARKER_DY, f.marker ?? "loop"));
  return {
    attrs: pruned({
      "data-frame-kind": f.kind,
      "data-frame-id": f.id,
      "data-frame-marker": f.marker,
      "data-diff": f.diff?.status,
      "data-owner-colored": oc ? "true" : undefined,
    }),
    opacity: accent.ghost ? t.ghostOpacity : undefined,
    shapes,
  };
}

const EDGE_COLOR: Record<BpmnEdge["style"], keyof ProcessTokens> = {
  seq: "edgeSeq",
  comp: "edgeComp",
  drop: "edgeDrop",
  reentry: "edgeReentry",
  assoc: "edgeAssoc",
};

function composeEdge(t: ProcessTokens, input: BpmnSceneInput, e: BpmnEdge): BpmnGroup | null {
  const g = bpmnEdgeGeom(input, e);
  if (!g) return null;
  const base = t[EDGE_COLOR[e.style]] as string;
  // Edge-level diff (engine-derived, endpoint rule): tint like the live view —
  // an inserted chain's internal edges at emerald/80, a removed run's at
  // rose/60 + ghost dash. `changed` is defensive (the engine never emits it).
  const tint = e.diff?.status;
  const stroke =
    tint === "added"
      ? alpha(t.diffAdded, 0.8)
      : tint === "removed"
        ? alpha(t.diffRemoved, 0.6)
        : tint === "changed"
          ? t.diffChanged
          : base;
  const dash = tint === "removed" ? "5 4" : EDGE_DASH[e.style];
  const marker = e.style === "assoc" ? undefined : e.style === "seq" ? ("solid" as const) : ("open" as const);
  const shapes: BpmnShape[] = [
    { kind: "path", d: g.d, fill: "none", stroke, strokeWidth: EDGE_STROKE, dash, marker },
  ];
  if (e.isDefault && g.horizontal) {
    // The default slash keeps the base style stroke (live view parity).
    shapes.push({ kind: "path", d: defaultSlashD(g.sx, g.sy), fill: "none", stroke: base, strokeWidth: EDGE_STROKE });
  }
  if (e.label) {
    const byId = new Map<string, BpmnNode>(input.nodes.map((n) => [n.id, n]));
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    if (a && b) {
      shapes.push({
        kind: "text",
        x: (gridCx(a.x) + gridCx(b.x)) / 2,
        y: (gridCy(a.y) + gridCy(b.y)) / 2 - EDGE_LABEL_DY,
        text: fitLabel(e.label, LABEL_W_EDGE, FONT_LABEL, t.fontFamily),
        fill: e.branchOf ? t.branchText : t.labelText,
        size: FONT_LABEL,
        anchor: "middle",
        italic: true,
      });
    }
  }
  return {
    attrs: pruned({
      "data-edge-style": e.style,
      "data-edge-diff": tint,
      "data-default-flow": e.isDefault ? "true" : undefined,
      "data-branch-pick": e.branchOf ? `${e.branchOf.frameId}:${e.branchOf.label}` : undefined,
    }),
    shapes,
  };
}

function pruned(attrs: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(attrs)) if (v !== undefined) out[k] = v;
  return out;
}

// ── Entry ────────────────────────────────────────────────────────────────────

/** Lane rail — band separators, pool borders + vertical spine titles, lane
 *  labels (plan 2026-07-07-bpmn-lanes-pools P4). Rail shapes live in
 *  UN-x-translated canvas space: the strip occupies x ∈ [0, railWidth) and
 *  separators/pool borders span the full canvas width. */
function composeLaneRail(
  t: ProcessTokens,
  input: BpmnSceneInput,
): { rail: BpmnGroup[]; railWidth: number } {
  const lanes = input.lanes ?? []; // defensive: hand-built legacy inputs
  const pools = input.pools ?? [];
  if (lanes.length === 0) return { rail: [], railWidth: 0 };

  const spineW = pools.length > 0 ? LANE_RAIL_CELL_W : 0;
  const labelW = lanes.some((l) => l.title !== null) ? LANE_RAIL_CELL_W : 0;
  const railWidth = spineW + labelW;
  const totalW = railWidth + PAD * 2 + input.width * CW;
  const top = (y0: number) => PAD + y0 * CH;
  const bottom = (y1: number) => PAD + (y1 + 1) * CH;
  const rail: BpmnGroup[] = [];

  // Band separators between consecutive lanes, full canvas width.
  const separators: BpmnShape[] = [];
  for (let i = 1; i < lanes.length; i++) {
    const y = top(lanes[i]!.y0);
    separators.push({
      kind: "line",
      x1: 0,
      y1: y,
      x2: totalW,
      y2: y,
      stroke: t.laneHeaderStroke,
      strokeWidth: LANE_SEPARATOR_STROKE,
    });
  }
  // Rail/grid divider.
  if (railWidth > 0) {
    separators.push({
      kind: "line",
      x1: railWidth,
      y1: top(lanes[0]!.y0),
      x2: railWidth,
      y2: bottom(lanes[lanes.length - 1]!.y1),
      stroke: t.laneHeaderStroke,
      strokeWidth: LANE_SEPARATOR_STROKE,
    });
  }
  if (separators.length) rail.push({ attrs: { "data-lane-rail": "separators" }, shapes: separators });

  const rotated = (text: string, cx: number, cy: number, weight?: number): TextShape => ({
    kind: "text",
    x: cx,
    y: cy + LANE_LABEL_SIZE / 2 - 1,
    text,
    fill: t.mutedText,
    size: LANE_LABEL_SIZE,
    anchor: "middle",
    ...(weight ? { weight } : {}),
    rotate: { deg: -90, cx, cy },
  });

  // Lane label cells (the untitled default lane draws none).
  for (const l of lanes) {
    if (l.title === null || labelW === 0) continue;
    const y0 = top(l.y0);
    const y1 = bottom(l.y1);
    // Rotated -90°, so the band's HEIGHT is the label's width budget.
    const budget = Math.max(3 * LANE_LABEL_CHAR_H, y1 - y0 - LANE_LABEL_PAD);
    rail.push({
      attrs: { "data-lane": l.id },
      title: l.title,
      shapes: [rotated(fitLabel(l.title, budget, LANE_LABEL_SIZE, t.fontFamily), spineW + labelW / 2, (y0 + y1) / 2)],
    });
  }

  // Pools: outer border (full width) + vertical spine title.
  for (const p of pools) {
    const members = lanes.filter((l) => l.poolId === p.id);
    if (members.length === 0) continue;
    const y0 = top(Math.min(...members.map((l) => l.y0)));
    const y1 = bottom(Math.max(...members.map((l) => l.y1)));
    const budget = Math.max(3 * LANE_LABEL_CHAR_H, y1 - y0 - LANE_LABEL_PAD);
    rail.push({
      attrs: { "data-pool": p.id },
      title: p.title,
      shapes: [
        {
          kind: "rect",
          x: POOL_BORDER_STROKE / 2,
          y: y0,
          w: totalW - POOL_BORDER_STROKE,
          h: y1 - y0,
          rx: POOL_BORDER_RADIUS,
          fill: "none",
          stroke: t.frameStroke,
          strokeWidth: POOL_BORDER_STROKE,
        },
        rotated(fitLabel(p.title, budget, LANE_LABEL_SIZE, t.fontFamily, FONT_WEIGHT_MEDIUM), spineW / 2, (y0 + y1) / 2, FONT_WEIGHT_MEDIUM),
      ],
    });
  }

  return { rail, railWidth };
}

export function composeBpmn(input: BpmnSceneInput, opts: ComposeBpmnOptions = {}): ComposedBpmn {
  const t = opts.tokens ?? defaultProcessTokens();
  // Actors keep their signifying emerald (committed chip: fill-emerald-300);
  // module owners take the caller's colour, else the local hash.
  const ownerColor = (o: BpmnOwner): string => opts.ownerColor?.(o) ?? t.actorText;

  // Reserve headroom for `go` back-arcs — their apex rises above the top
  // track and would clip at the viewport otherwise.
  const byId = new Map<string, BpmnNode>(input.nodes.map((n) => [n.id, n]));
  let headroom = 0;
  for (const e of input.edges) {
    if (e.style !== "reentry") continue;
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    if (a && b) headroom = Math.max(headroom, reentryLift(Math.abs(a.x - b.x)));
  }

  const edges: BpmnGroup[] = [];
  const markers = { solid: false, open: false };
  for (const e of input.edges) {
    const grp = composeEdge(t, input, e);
    if (!grp) continue;
    for (const s of grp.shapes) {
      if (s.kind === "path" && s.marker === "solid") markers.solid = true;
      if (s.kind === "path" && s.marker === "open") markers.open = true;
    }
    edges.push(grp);
  }

  const nodes = input.nodes.flatMap((n) => {
    if (n.kind === "task") {
      const g = composeTask(t, ownerColor, n);
      return n.danglingEngine ? [g, composeEngineBadge(t, n)] : [g];
    }
    return [
      n.kind === "gateway"
        ? composeGateway(t, ownerColor, n)
        : n.kind === "event"
          ? composeEvent(t, ownerColor, n)
          : n.kind === "system"
            ? composeSystem(t, n)
            : composeAnnotation(t, ownerColor, n),
    ];
  });

  const { rail, railWidth } = composeLaneRail(t, input);

  return {
    width: railWidth + PAD * 2 + input.width * CW,
    height: PAD * 2 + input.height * CH + headroom,
    headroom,
    railWidth,
    rail,
    frames: input.frames.map((f) => composeFrame(t, ownerColor, f)),
    edges,
    nodes,
    markers,
  };
}
